import { ScatterplotLayer, ScatterplotLayerProps } from "@deck.gl/layers";
import { Feature, Position } from "geojson";
import { useSelector } from "react-redux";

import { NodeIconLayer, NodeIconLayerProps } from "@nens/deck-gl";

import { guiShouldAllowSelectingAssetsOrPoints } from "../../state/guiStateHelper";
import { getLayers } from "../../state/layerOneDMenu";
import { getZoom } from "../../state/map";
import { getPumpActivities } from "../../state/pumpActivity";
import { getSelectedAsset } from "../../state/selectedAsset";
import { Asset } from "../../types/assets";

import { ICON_MAPPING, getLayerColors } from "./DeckGlConsts";
import useTimeAnimation from "./helpers/useTimeAnimation";

// Helper function to convert line to point at the
// center of the line. Right now, only use this for weirs only as
// weir is very short line which is better represented by point.
const getCenterIfTwoPoints = (coordinates: Position | Position[]): [number, number] => {
  if (typeof coordinates[0] === "number" || typeof coordinates[1] === "number")
    return coordinates as [number, number];
  const firstPoint = coordinates[0]; // first point of the line
  const secondPoint = coordinates[1]; // second point of the line

  // Coordinates of the center point of the line
  return [(firstPoint[0] + secondPoint[0]) / 2, (firstPoint[1] + secondPoint[1]) / 2];
};

const useNodeLayer = (
  setSelectedAsset: (id: string) => void,
  assets?: { [value: string]: Asset[] }
) => {
  const zoom = useSelector(getZoom) || 0;
  const isSelectable = useSelector(guiShouldAllowSelectingAssetsOrPoints);
  const selectedAsset = useSelector(getSelectedAsset);
  const oneDLayersStyle = useSelector(getLayers);
  const pumpActivities = useSelector(getPumpActivities);

  const COLORS = getLayerColors(oneDLayersStyle);

  const { timeAnimation } = useTimeAnimation();

  const selectItem = (feature: Feature) => {
    if (feature && feature.properties && isSelectable) {
      setSelectedAsset(feature.properties.site_id);
    }
  };

  const defaultProps = {
    // all
    id: "scatterplot-layer", //should be overriden
    data: [], //should be overriden
    getPosition: (d) => {
      if (!("line_geometry" in d.properties) || !d.properties.line_geometry)
        return getCenterIfTwoPoints(d.geometry.coordinates);
      // This is only for pumps
      const [coord1, coord2, coord3, coord4] = d.properties.line_geometry.coordinates;
      return getCenterIfTwoPoints([
        [coord1, coord2],
        [coord3, coord4],
      ]);
    },
    pickable: true,
    onClick: (info) => selectItem(info.object),

    // Scatterplot specific
    stroked: true,
    filled: true,
    antialiasing: true,
    radiusUnits: "pixels",
    getFillColor: COLORS.WHITE,
    lineWidthMinPixels: 2,
    getLineColor: COLORS.BLACK,
  } as ScatterplotLayerProps<Asset>;

  // Icon Specific
  const iconLayerProps = {
    getRadius: zoom / 2,
    getLineWidth: 0.1,
    getLineColor: COLORS.WHITE,
    iconAtlas: "./assets/images/iconAtlas.png",
    iconMapping: ICON_MAPPING,
    sizeUnits: "pixels",
    getIconSize: zoom * 1.5,
    getIconColor: COLORS.GREY,
  };

  // Pumps rotate props
  const rotateProps = {
    getRotate: (d: Feature) => {
      const thisPumpActivity =
        d.properties && d.properties.id !== undefined && pumpActivities[d.properties.id];
      return !!thisPumpActivity && thisPumpActivity > 0 ? 1 : 0;
    },
    ...timeAnimation,
    updateTriggers: {
      getRotate: pumpActivities,
    },
  };
  if (!assets) return [];

  return [
    new ScatterplotLayer({
      ...defaultProps,
      id: "nodes-layer",
      data: assets.nodes,
      radiusMaxPixels: 6.5,
      radiusUnits: "meters",
      getRadius: 2,
      lineWidthMaxPixels: 1,
      lineWidthUnits: "meters",
      getLineWidth: 0.005,
      visible: oneDLayersStyle.nodes.visible,
    }),
    new NodeIconLayer({
      ...defaultProps,
      ...iconLayerProps,
      id: "icon-manholes-layer",
      data: assets.manholes,
      getIcon: () => "manholes",
      radiusMaxPixels: 6.5,
      radiusUnits: "meters",
      getRadius: 2,
      lineWidthMaxPixels: 1,
      lineWidthUnits: "meters",
      getLineWidth: 0.005,
      sizeUnits: "meters",
      getIconSize: 5,
      sizeMaxPixels: 15.5,
      visible: oneDLayersStyle.manholes.visible,
    } as NodeIconLayerProps),
    new NodeIconLayer({
      ...defaultProps,
      ...iconLayerProps,
      id: "icon-weirs-layer",
      data: assets.weirs,
      getIcon: () => "weirs",
      visible: oneDLayersStyle.weirs.visible,
    } as NodeIconLayerProps),
    new NodeIconLayer({
      ...defaultProps,
      ...iconLayerProps,
      id: "icon-breaches-layer",
      data: assets.breaches,
      getIcon: () => "breaches",
      getIconSize: zoom * 1.1,
      getLineColor: COLORS.BREACHES,
      getFillColor: COLORS.BREACHES,
      visible: oneDLayersStyle.breaches.visible,
    } as NodeIconLayerProps),
    new NodeIconLayer({
      ...defaultProps,
      ...iconLayerProps,
      ...rotateProps,
      id: "icon-pumps-layer",
      data: assets.pumps,
      getIcon: () => "pumps",
      visible: oneDLayersStyle.pumps.visible,
    } as NodeIconLayerProps),
    new NodeIconLayer({
      ...defaultProps,
      ...iconLayerProps,
      ...rotateProps,
      id: "icon-selected-layer",
      data: selectedAsset ? [selectedAsset] : [],
      getIcon: (d) => d.properties.model_type,
      getIconSize: (d) => zoom * (d.properties.model_type === "breaches" ? 1.1 : 1.5),
      getFillColor: COLORS.SELECTED,
      getLineColor: COLORS.SELECTED,
      visible:
        !!selectedAsset &&
        (selectedAsset.properties.model_type === "nodes" ||
          selectedAsset.properties.model_type === "manholes" ||
          selectedAsset.properties.model_type === "pumps" ||
          selectedAsset.properties.model_type === "weirs" ||
          selectedAsset.properties.model_type === "breaches"),
    } as NodeIconLayerProps),
  ];
};

export default useNodeLayer;
